// ─── FDI Tooth Numbering ───

// Primary (deciduous/baby) teeth
const PRIMARY_UPPER_RIGHT = [55, 54, 53, 52, 51];
const PRIMARY_UPPER_LEFT = [61, 62, 63, 64, 65];
const PRIMARY_LOWER_RIGHT = [85, 84, 83, 82, 81];
const PRIMARY_LOWER_LEFT = [71, 72, 73, 74, 75];

// Adult (permanent) teeth
const UPPER_RIGHT = [18, 17, 16, 15, 14, 13, 12, 11];
const UPPER_LEFT = [21, 22, 23, 24, 25, 26, 27, 28];
const LOWER_LEFT = [38, 37, 36, 35, 34, 33, 32, 31];
const LOWER_RIGHT = [41, 42, 43, 44, 45, 46, 47, 48];

// ─── Tooth State Types ───

export interface ToothCondition {
  code: string;
  surfaces: string[];
}

export interface ToothState {
  flags: string[]; // 'ok' | 'missing' | 'root_fragment' | 'implant' | 'impacted'
  conditions: ToothCondition[];
  crown: string | null;
}

export type ToothStatesMap = Record<string, ToothState>;

// ─── Condition Colors & Codes ───

const CONDITION_COLORS: Record<string, string> = {
  caries: '#dc2626',
  composite: '#3b82f6',
  amalgam: '#6b7280',
  recurrent_caries: '#f97316',
};

const CROWN_COLORS: Record<string, string> = {
  ceramic: '#e2e8f0',
  pfm: '#fbbf24',
  gold: '#f59e0b',
  metal: '#9ca3af',
};

const CROWN_CODES: Record<string, string> = {
  ceramic: 'CER',
  pfm: 'PFM',
  gold: 'GC',
  metal: 'MC',
};

const FLAG_CODES: Record<string, string> = {
  missing: 'M',
  root_fragment: 'RF',
  implant: 'IMP',
  impacted: 'MP',
};

// ─── Circular 5-surface tooth SVG ───

function ToothRingSvg({
  size,
  conditionSurfaces,
  crownColor,
  strokeColor,
  isMissing,
}: {
  size: number;
  conditionSurfaces: Record<string, string>;
  crownColor: string | null;
  strokeColor: string;
  isMissing: boolean;
}) {
  const cx = size / 2;
  const cy = size / 2;
  const outer = size * 0.46;
  const inner = size * 0.22;
  const stroke = strokeColor;

  // Arc helper for the 4 outer sections
  function arcPath(startAngle: number, endAngle: number, outerR: number, innerR: number): string {
    const toRad = (a: number) => (a * Math.PI) / 180;
    const sx1 = cx + outerR * Math.cos(toRad(startAngle));
    const sy1 = cy + outerR * Math.sin(toRad(startAngle));
    const ex1 = cx + outerR * Math.cos(toRad(endAngle));
    const ey1 = cy + outerR * Math.sin(toRad(endAngle));
    const sx2 = cx + innerR * Math.cos(toRad(endAngle));
    const sy2 = cy + innerR * Math.sin(toRad(endAngle));
    const ex2 = cx + innerR * Math.cos(toRad(startAngle));
    const ey2 = cy + innerR * Math.sin(toRad(startAngle));
    return `M ${sx1} ${sy1} A ${outerR} ${outerR} 0 0 1 ${ex1} ${ey1} L ${sx2} ${sy2} A ${innerR} ${innerR} 0 0 0 ${ex2} ${ey2} Z`;
  }

  // Map surfaces to angle ranges (top=buccal, right=distal, bottom=lingual, left=mesial)
  const surfaceArcs: { name: string; start: number; end: number }[] = [
    { name: 'buccal', start: -135, end: -45 }, // top
    { name: 'distal', start: -45, end: 45 }, // right
    { name: 'lingual', start: 45, end: 135 }, // bottom
    { name: 'mesial', start: 135, end: 225 }, // left
  ];

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block' }}>
      {/* Outer ring sections */}
      {surfaceArcs.map((arc) => {
        const fill = conditionSurfaces[arc.name];
        return (
          <path
            key={arc.name}
            d={arcPath(arc.start, arc.end, outer, inner)}
            fill={fill ?? '#fff'}
            stroke={stroke}
            strokeWidth={1}
            opacity={fill ? 0.85 : 1}
          />
        );
      })}

      {/* Center circle (occlusal) */}
      <circle
        cx={cx}
        cy={cy}
        r={inner}
        fill={conditionSurfaces.occlusal ?? crownColor ?? '#fff'}
        stroke={stroke}
        strokeWidth={1}
        opacity={conditionSurfaces.occlusal ? 0.85 : 1}
      />

      {/* Crown half-fill overlay */}
      {crownColor && !conditionSurfaces.occlusal && (
        <circle cx={cx} cy={cy} r={inner - 1} fill={crownColor} opacity={0.6} />
      )}

      {/* Missing X overlay */}
      {isMissing && (
        <>
          <line
            x1={cx - outer + 2}
            y1={cy - outer + 2}
            x2={cx + outer - 2}
            y2={cy + outer - 2}
            stroke="#ef4444"
            strokeWidth={2}
          />
          <line
            x1={cx + outer - 2}
            y1={cy - outer + 2}
            x2={cx - outer + 2}
            y2={cy + outer - 2}
            stroke="#ef4444"
            strokeWidth={2}
          />
        </>
      )}
    </svg>
  );
}

// ─── Single Tooth Cell ───

function ToothCell({
  num,
  state,
  isUpper,
  onClick,
  selected,
  readOnly,
  primary,
}: {
  num: number;
  state: ToothState | undefined;
  isUpper: boolean;
  onClick: (num: number) => void;
  selected: boolean;
  readOnly: boolean;
  primary: string;
}) {
  const size = 52;
  const isOk = !state || state.flags.includes('ok');
  const isMissing = state?.flags.includes('missing') ?? false;
  const hasConditions = (state?.conditions.length ?? 0) > 0;
  const hasCrown = !!state?.crown;

  // Build surface color map
  const conditionSurfaces: Record<string, string> = {};
  if (state) {
    for (const cond of state.conditions) {
      const color = CONDITION_COLORS[cond.code] ?? '#94a3b8';
      for (const surf of cond.surfaces) {
        conditionSurfaces[surf] = color;
      }
    }
  }

  const strokeColor = selected ? primary : '#d1d5db';
  const crownColor = hasCrown ? (CROWN_COLORS[state!.crown!] ?? '#94a3b8') : null;

  // Build status label (flag code or crown code)
  let statusLabel = '';
  if (state) {
    const flagCodes = state.flags
      .filter((f) => f !== 'ok')
      .map((f) => FLAG_CODES[f] ?? '')
      .filter(Boolean);
    if (flagCodes.length > 0) statusLabel = flagCodes.join('/');
    if (hasCrown) statusLabel = CROWN_CODES[state.crown!] ?? '';
  }

  const numberBadge = (
    <div
      style={{
        height: 22,
        minWidth: 30,
        padding: '0 6px',
        borderRadius: 11,
        background: selected ? primary : hasConditions || hasCrown || !isOk ? primary : '#e0f2fe',
        color: selected || hasConditions || hasCrown || !isOk ? '#fff' : '#0ea5e9',
        fontSize: 11,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 1,
      }}
    >
      {num}
    </div>
  );

  const checkOrLabel = (
    <div
      style={{
        height: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 10,
        fontWeight: 700,
        color: statusLabel ? '#64748b' : '#94a3b8',
        lineHeight: 1,
      }}
    >
      {statusLabel || (isOk ? '✓' : '')}
    </div>
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        cursor: readOnly ? 'default' : 'pointer',
        opacity: isMissing ? 0.45 : 1,
        padding: '0 1px',
      }}
      onClick={() => {
        if (!readOnly) onClick(num);
      }}
    >
      {isUpper ? (
        <>
          {numberBadge}
          {checkOrLabel}
          <ToothRingSvg
            size={size}
            conditionSurfaces={conditionSurfaces}
            crownColor={crownColor}
            strokeColor={strokeColor}
            isMissing={isMissing}
          />
        </>
      ) : (
        <>
          <ToothRingSvg
            size={size}
            conditionSurfaces={conditionSurfaces}
            crownColor={crownColor}
            strokeColor={strokeColor}
            isMissing={isMissing}
          />
          {checkOrLabel}
          {numberBadge}
        </>
      )}
    </div>
  );
}

// ─── Legend ───

export function OdontogramLegend() {
  const items = [
    { label: 'OK', code: '✓', color: '#fff', border: '#d1d5db' },
    { label: 'Missing', code: 'M', color: '#fff', border: '#ef4444' },
    { label: 'Root Fragment', code: 'RF', color: '#fff', border: '#f97316' },
    { label: 'Implant', code: 'IMP', color: '#fff', border: '#8b5cf6' },
    { label: 'Impacted', code: 'MP', color: '#fff', border: '#eab308' },
    { label: 'Caries', code: null, color: '#dc2626', border: 'none', fill: true },
    { label: 'Composite Filling', code: null, color: '#3b82f6', border: 'none', fill: true },
    { label: 'Amalgam Filling', code: null, color: '#6b7280', border: 'none', fill: true },
    { label: 'Recurrent Caries', code: null, color: '#f97316', border: 'none', fill: true },
    {
      label: 'Porcelain Fused to Metal Crown (PFM)',
      code: null,
      color: '#fbbf24',
      border: 'none',
      fill: true,
    },
    { label: 'Ceramic Crown (CER)', code: null, color: '#e2e8f0', border: '#94a3b8', fill: true },
    { label: 'Gold Crown (GC)', code: null, color: '#f59e0b', border: 'none', fill: true },
    { label: 'Metal Crown (MC)', code: null, color: '#9ca3af', border: 'none', fill: true },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
        }}
      >
        Tooth Chart Legend
      </span>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        {items.map((item) => (
          <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {item.code ? (
              <span
                style={{
                  width: 22,
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#64748b',
                  textAlign: 'center',
                  flexShrink: 0,
                }}
              >
                {item.code}
              </span>
            ) : (
              <div
                style={{
                  width: 22,
                  height: 14,
                  borderRadius: 3,
                  background: item.color,
                  border: item.border !== 'none' ? `1.5px solid ${item.border}` : 'none',
                  flexShrink: 0,
                }}
              />
            )}
            <span
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text)',
                lineHeight: 1.3,
              }}
            >
              {item.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Tooth Row helper ───

function ToothRow({
  left,
  right,
  isUpper,
  toothStates,
  selectedTooth,
  onToothClick,
  readOnly,
  primary,
}: {
  left: readonly number[];
  right: readonly number[];
  isUpper: boolean;
  toothStates: ToothStatesMap;
  selectedTooth: number | null;
  onToothClick: (num: number) => void;
  readOnly: boolean;
  primary: string;
}) {
  return (
    <div style={{ display: 'flex', gap: 0, flexWrap: 'wrap', justifyContent: 'center' }}>
      {left.map((num) => (
        <ToothCell
          key={num}
          num={num}
          state={toothStates[String(num)]}
          isUpper={isUpper}
          onClick={onToothClick}
          selected={selectedTooth === num}
          readOnly={readOnly}
          primary={primary}
        />
      ))}
      <div style={{ width: 8, flexShrink: 0 }} />
      {right.map((num) => (
        <ToothCell
          key={num}
          num={num}
          state={toothStates[String(num)]}
          isUpper={isUpper}
          onClick={onToothClick}
          selected={selectedTooth === num}
          readOnly={readOnly}
          primary={primary}
        />
      ))}
    </div>
  );
}

// ─── Main Chart ───

interface OdontogramChartProps {
  toothStates: ToothStatesMap;
  selectedTooth: number | null;
  onToothClick: (num: number) => void;
  readOnly?: boolean;
  primary: string;
}

export function OdontogramChart({
  toothStates,
  selectedTooth,
  onToothClick,
  readOnly = false,
  primary,
}: OdontogramChartProps) {
  const rowProps = { toothStates, selectedTooth, onToothClick, readOnly, primary };

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)', alignItems: 'center' }}
    >
      {/* Primary upper teeth */}
      <ToothRow left={PRIMARY_UPPER_RIGHT} right={PRIMARY_UPPER_LEFT} isUpper {...rowProps} />

      {/* Separator */}
      <div style={{ width: '100%', maxWidth: 900, height: 1, background: 'var(--border)' }} />

      {/* Adult upper teeth */}
      <ToothRow left={UPPER_RIGHT} right={UPPER_LEFT} isUpper {...rowProps} />

      {/* Jaw separator */}
      <div
        style={{ width: '100%', maxWidth: 900, height: 2, background: 'var(--text-placeholder)' }}
      />

      {/* Adult lower teeth */}
      <ToothRow left={LOWER_RIGHT} right={LOWER_LEFT} isUpper={false} {...rowProps} />

      {/* Separator */}
      <div style={{ width: '100%', maxWidth: 900, height: 1, background: 'var(--border)' }} />

      {/* Primary lower teeth */}
      <ToothRow
        left={PRIMARY_LOWER_RIGHT}
        right={PRIMARY_LOWER_LEFT}
        isUpper={false}
        {...rowProps}
      />
    </div>
  );
}
